import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination as SwiperPagination } from "swiper/modules";
import { useParams } from "react-router-dom";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "../../../assets/css/PhotoNewsSection.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(dateStr) {
  if (!dateStr) return { month: "N/A", day: "--", year: "----" };
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime()))
    return { month: "N/A", day: "--", year: "----" };
  return {
    month: date.toLocaleString("en", { month: "short" }).toUpperCase(),
    day: String(date.getDate()).padStart(2, "0"),
    year: date.getFullYear(),
  };
}

function truncate(text, maxLength) {
  if (!text) return "";
  return text.length > maxLength
    ? `${text.substring(0, maxLength).trim()}…`
    : text;
}

function PhotoNewsSection() {
  const { section, id } = useParams();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 12,
  });

  useEffect(() => {
    let active = true;

    async function loadAlbums() {
      setLoading(true);
      setError(false);
      try {
        const perPage = section === "Photo Gallery" ? 12 : 3;
        const requestedPage =
          section && section !== "Home" && section !== "Photo Gallery"
            ? Math.floor(Math.random() * 5) + 1
            : 1;

        const response = await axios.get(
          `${BASE_URL}galdata?page=${requestedPage}&per_page=${perPage}`,
        );

        if (!active) return;
        setAlbums(response?.data?.allData || []);
        setPagination(
          response?.data?.pagination || {
            current_page: requestedPage,
            last_page: 1,
            total: 0,
            per_page: perPage,
          },
        );
      } catch (err) {
        if (active) {
          console.error("Gallery load failed:", err);
          setError(true);
          setAlbums([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    loadAlbums();
    return () => {
      active = false;
    };
  }, [section, id, retryKey]);

  const goToPage = async (page) => {
    const current = Number(pagination?.current_page || 1);
    const last = Math.max(1, Number(pagination?.last_page || 1));
    if (page < 1 || page > last || page === current) return;

    try {
      setLoading(true);
      setError(false);
      const perPage = section === "Photo Gallery" ? 12 : 3;
      const response = await axios.get(
        `${BASE_URL}galdata?page=${page}&per_page=${perPage}`,
      );
      setAlbums(response?.data?.allData || []);
      setPagination(
        response?.data?.pagination || {
          current_page: page,
          last_page: last,
          total: 0,
          per_page: perPage,
        },
      );
    } catch (err) {
      console.error("Gallery pagination failed:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAlbum = (album) => {
    setActiveAlbum({
      galleryTithle: album.galleryTithle,
      caption: album.caption,
      datesd: album.datesd,
      images: album.photos || [],
    });
    setShowModal(true);
  };

  const closeAlbum = () => setShowModal(false);

  if (!loading && !error && albums.length === 0) return null;

  return (
    <section
      id="photo-news"
      className="fmw-photo-gallery"
      aria-label="Photo Gallery"
    >
      <div className="container">
        <header className="fmw-photo-heading">
          <div>
            <span className="fmw-photo-kicker">
              MOMENTS · PEOPLE · PROGRESS
            </span>
            <h2>Photo News</h2>
          </div>
          <p className="fmw-journal-section-label">
            <i className="fa-solid fa-images" aria-hidden="true"></i>
          </p>
        </header>

        {loading && (
          <div className="fmw-photo-status" role="status" aria-live="polite">
            <span className="fmw-photo-spinner" />
            Loading photo albums…
          </div>
        )}

        {error && albums.length === 0 && (
          <div className="fmw-photo-message" role="alert">
            <h3>Photo gallery temporarily unavailable.</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              onClick={() => setRetryKey((value) => value + 1)}
            >
              Try Again
            </button>
          </div>
        )}

        {!error && albums.length > 0 && (
          <>
            <div className="fmw-photo-grid" aria-busy={loading}>
              {albums.map((album, index) => {
                const { month, day, year } = formatDate(album.datesd);
                const imageUrl = album.pic
                  ? `${BACKEND_URL}upload/${album.pic}`
                  : `${BACKEND_URL}upload/general_images/no_camera-photo.png`;

                return (
                  <article
                    className="fmw-photo-card"
                    key={album.trackerf || index}
                  >
                    <button
                      type="button"
                      className="fmw-photo-card-button"
                      onClick={() => handleOpenAlbum(album)}
                      aria-label={`Open photo album: ${album.galleryTithle || "Photo album"}`}
                    >
                      <div className="fmw-photo-image-wrap">
                        <img
                          loading="lazy"
                          decoding="async"
                          src={imageUrl}
                          alt={album.galleryTithle || "Ministry photo album"}
                          className="fmw-photo-card-image"
                        />
                        <span className="fmw-photo-date">
                          <span>{month}</span>
                          <strong>{day}</strong>
                          <span>{year}</span>
                        </span>
                        <span className="fmw-photo-open" aria-hidden="true">
                          <i className="fa-solid fa-arrow-up-right-from-square" />
                        </span>
                      </div>
                      <div className="fmw-photo-card-copy">
                        <span className="fmw-photo-card-eyebrow">
                          PHOTO ALBUM
                        </span>
                        <h3>{truncate(album.galleryTithle, 100)}</h3>
                        <div className="fmw-photo-card-meta">
                          <span>
                            <i
                              className="fa-regular fa-images"
                              aria-hidden="true"
                            />{" "}
                            {album.photo_count || 0} photos
                          </span>
                          <span className="fmw-photo-view">
                            View album <b aria-hidden="true">→</b>
                          </span>
                        </div>
                      </div>
                    </button>
                  </article>
                );
              })}
            </div>

            <nav
              className="fmw-photo-pagination"
              aria-label="Photo gallery pagination"
            >
              <span>
                PAGE <strong>{pagination.current_page}</strong> OF{" "}
                {pagination.last_page}
              </span>
              <div className="fmw-photo-page-actions">
                <button
                  type="button"
                  onClick={() => goToPage(1)}
                  disabled={pagination.current_page <= 1 || loading}
                  aria-label="First page"
                >
                  «
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(Number(pagination.current_page) - 1)}
                  disabled={pagination.current_page <= 1 || loading}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(Number(pagination.current_page) + 1)}
                  disabled={
                    pagination.current_page >= pagination.last_page || loading
                  }
                  aria-label="Next page"
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(Number(pagination.last_page))}
                  disabled={
                    pagination.current_page >= pagination.last_page || loading
                  }
                  aria-label="Last page"
                >
                  »
                </button>
              </div>
            </nav>
          </>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={closeAlbum}
        size="xl"
        centered
        scrollable
        className="fmw-photo-modal"
      >
        <Modal.Body className="fmw-photo-modal-body">
          {activeAlbum && (
            <>
              <header className="fmw-photo-modal-header">
                <div>
                  <span className="fmw-photo-kicker">FMW PHOTO ARCHIVE</span>
                  <h3>{activeAlbum.galleryTithle}</h3>
                  <p className="fmw-photo-modal-date">
                    {formatDate(activeAlbum.datesd).month}{" "}
                    {formatDate(activeAlbum.datesd).day},{" "}
                    {formatDate(activeAlbum.datesd).year}
                  </p>
                  {activeAlbum.caption && (
                    <p className="fmw-photo-modal-caption">
                      {activeAlbum.caption}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="fmw-photo-close"
                  onClick={closeAlbum}
                  aria-label="Close photo album"
                >
                  ×
                </button>
              </header>

              <div className="fmw-photo-share-row">
                <span>
                  {activeAlbum.images?.length || 0} images in this album
                </span>
              </div>

              <Swiper
                modules={[Navigation, SwiperPagination]}
                navigation
                pagination={{ clickable: true }}
                loop={(activeAlbum?.images?.length || 0) > 1}
                className="fmw-photo-swiper"
              >
                {activeAlbum.images?.map((image, index) => (
                  <SwiperSlide key={image.id || image.pic || index}>
                    <img
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      src={`${BACKEND_URL}upload/${image.pic}`}
                      alt={`${activeAlbum.galleryTithle || "Photo album"} — image ${index + 1}`}
                      className="fmw-photo-slide-image"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </>
          )}
        </Modal.Body>
      </Modal>
    </section>
  );
}

export default React.memo(PhotoNewsSection);
